/**
 * @fileoverview Signature widget — Mode C event signature capture.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Phase 3 renders a presentational capture button + thumbnail. Phase 4
 *   wires the real `<SignaturePad>` from `@firesquirrel/attachments`. The
 *   captured `AttachmentRefLike` is stored as the field value but extracted
 *   at completion time into `events[].signatures[]` per D-0.16.
 *
 * @component SignatureField
 */
import { useState } from 'react';
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import type { AttachmentRefLike } from '@firesquirrel/core-domain';
import { inferSignatureRole } from '@firesquirrel/core-domain';
import { useAttachmentPicker, useIsOffline } from '../contracts/rendererContext';

export function SignatureField(props: BaseWidgetProps<AttachmentRefLike | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, bulkPilotMode, errorMessage, requiredUnmet } = props;
  const picker = useAttachmentPicker();
  const isOffline = useIsOffline();
  const disabledByPolicy = !!resolved?.disabled || !!bulkPilotMode || !picker;
  const [captureError, setCaptureError] = useState<string | null>(null);

  // D-0.16 signature role inference — explicit extras.signatureRole wins;
  // heuristic fallback derived from label; 'other' when unclassifiable.
  const sigRole = inferSignatureRole({
    label: field.label,
    extras: field.extras as Record<string, unknown> | undefined,
  });

  async function capture() {
    if (disabledByPolicy || !picker) return;
    setCaptureError(null);
    try {
      const ref = await picker.captureSignature({
        parentType: 'eventSignature',
        parentId: field.id,
        kind: 'signature',
      });
      onChange(ref);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      // User-cancelled is not an error — silently ignore.
      if (msg === 'SIGNATURE_CANCELLED') return;
      console.error('[SignatureField] capture failed:', msg);
      setCaptureError(
        msg === 'SIGNATURE_STAGING_FAILED' || msg === 'SIGNATURE_NOT_WIRED'
          ? 'Failed to save signature. Please try again.'
          : 'Signature capture failed. Please try again.',
      );
    }
  }

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="signature"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={errorMessage}
      requiredUnmet={requiredUnmet}
    >
      <div data-signature-role={sigRole.role} data-signature-role-label={sigRole.roleLabel ?? ''} className="flex flex-col gap-1">
        {isOffline && (
          <span data-pill="offline" role="status" className="self-start inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
            offline
          </span>
        )}
        {value ? (
          <div className="flex items-center gap-2">
            <span role="img" aria-label="Signature captured" data-attachment-id={value._id} className="inline-flex items-center justify-center w-16 h-10 border border-green-300 rounded-sm bg-green-50 text-green-700 text-xs font-medium">
              ✓ Signed
            </span>
            {!disabledByPolicy && (
              <button type="button" onClick={() => { setCaptureError(null); onChange(null); }} className="text-red-500 text-xs">
                Clear
              </button>
            )}
          </div>
        ) : (
          <button
            id={field.id}
            type="button"
            data-action="capture-signature"
            disabled={disabledByPolicy}
            onClick={capture}
            aria-required={!!(field.required || resolved?.required)}
            className="px-3 py-2 text-sm border border-dashed border-gray-300 rounded-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Capture signature
          </button>
        )}
        {captureError && (
          <p className="text-xs text-red-600 mt-1">{captureError}</p>
        )}
      </div>
    </FieldWrapper>
  );
}
